// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { isBankUploadDataSection } from '@client/banks/layouts/section-tabs/bank-upload-section-tabs';
import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import type { BankUploadSection } from '@client/banks/types/bank-upload';
import { useConfirmPopup } from '@shared/ui-kit/components/popups/confirm-popup/confirm-popup';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type DrawerHeaderMode = 'default' | 'connect' | 'bank';

export const useBankUploadDrawerHeader = () => {
  const { onClose, resetFormData, formData } = useBankUploadData();
  const { reset, meta } = useStepsData();
  const { t } = useTranslation();
  const showConfirmDiscard = useConfirmPopup();

  const section = meta?.section as BankUploadSection | undefined;

  const showDiscard = useMemo(
    () => section !== undefined && section !== 'flow' && section !== 'connect',
    [section],
  );

  const showClose = useMemo(() => !showDiscard, [showDiscard]);

  const headerMode = useMemo<DrawerHeaderMode>(() => {
    if (isBankUploadDataSection(section) || section === 'review') {
      return 'bank';
    }

    if (section === 'connect') {
      return 'connect';
    }

    return 'default';
  }, [section]);

  const bankName = useMemo(
    () => formData.institution.name,
    [formData.institution.name],
  );

  const logoUrl = useMemo(
    () => formData.institution.logoUrl?.trim() ?? '',
    [formData.institution.logoUrl],
  );

  const showLogoPreview = useMemo(() => {
    if (!logoUrl) {
      return false;
    }

    try {
      const parsed = new URL(logoUrl);

      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }, [logoUrl]);

  const discardDraft = useCallback(() => {
    showConfirmDiscard({
      title: t('aggregator.create-bank.discard.title', { ns: 'aggregator' }),
      message: t('aggregator.create-bank.discard.description', {
        ns: 'aggregator',
      }),
      confirmText: t('aggregator.create-bank.discard.confirm', {
        ns: 'aggregator',
      }),
      cancelText: t('common.cancel', { ns: 'common' }),
      onConfirm: () => {
        resetFormData();
        reset();
        onClose();
      },
    });
  }, [onClose, reset, resetFormData, showConfirmDiscard, t]);

  return {
    discardDraft,
    onClose,
    showDiscard,
    showClose,
    headerMode,
    bankName,
    logoUrl,
    showLogoPreview,
    t,
  };
};
